import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { WorkforceCache } from "@/services/workforceCache";

interface AdicionarNovoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdicionado: () => void;
}

export default function AdicionarNovoModal({
  open,
  onOpenChange,
  onAdicionado,
}: AdicionarNovoModalProps) {
  const [colaboradorData, setColaboradorData] = useState({
    nome: "",
    profissao: "",
    telefone: "",
    email: "",
    observacoes: "",
  });

  const [profissaoData, setProfissaoData] = useState({
    nome: "",
    descricao: "",
  });

  const resetForms = () => {
    setColaboradorData({
      nome: "",
      profissao: "",
      telefone: "",
      email: "",
      observacoes: "",
    });
    setProfissaoData({
      nome: "",
      descricao: "",
    });
  };

  const handleSaveColaborador = () => {
    if (!colaboradorData.nome.trim()) {
      toast({
        title: "Campo obrigatório",
        description: "Nome do colaborador é obrigatório.",
        variant: "destructive"
      });
      return;
    }

    const novoColaborador = {
      id: `c_${Date.now()}`,
      nome: colaboradorData.nome.trim(),
      profissao: colaboradorData.profissao.trim(),
      telefone: colaboradorData.telefone.trim(),
      email: colaboradorData.email.trim(),
      endereco: "",
      observacoes: colaboradorData.observacoes.trim(),
      ativo: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    WorkforceCache.addColaborador(novoColaborador);

    toast({
      title: "Colaborador adicionado",
      description: `${colaboradorData.nome} foi adicionado com sucesso.`,
    });

    resetForms();
    onAdicionado();
    onOpenChange(false);
  };

  const handleSaveProfissao = () => {
    if (!profissaoData.nome.trim()) {
      toast({
        title: "Campo obrigatório",
        description: "Nome da profissão é obrigatório.",
        variant: "destructive"
      });
      return;
    }

    const novaProfissao = {
      id: `p_${Date.now()}`,
      nome: profissaoData.nome.trim(),
      descricao: profissaoData.descricao.trim(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    WorkforceCache.addProfissao(novaProfissao);

    toast({
      title: "Profissão adicionada",
      description: `${profissaoData.nome} foi adicionada com sucesso.`,
    });

    resetForms();
    onAdicionado();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adicionar Novo</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="colaborador" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="colaborador">Colaborador</TabsTrigger>
            <TabsTrigger value="profissao">Profissão</TabsTrigger>
          </TabsList>

          <TabsContent value="colaborador" className="space-y-4">
            <div className="space-y-3">
              <div>
                <Label htmlFor="nome-colaborador">Nome *</Label>
                <Input
                  id="nome-colaborador"
                  value={colaboradorData.nome}
                  onChange={(e) => setColaboradorData(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Nome do colaborador"
                />
              </div>

              <div>
                <Label htmlFor="profissao-colaborador">Profissão</Label>
                <Input
                  id="profissao-colaborador"
                  value={colaboradorData.profissao}
                  onChange={(e) => setColaboradorData(prev => ({ ...prev, profissao: e.target.value }))}
                  placeholder="Ex: Pedreiro, Eletricista..."
                />
              </div>

              <div>
                <Label htmlFor="telefone-colaborador">Telefone</Label>
                <Input
                  id="telefone-colaborador"
                  value={colaboradorData.telefone}
                  onChange={(e) => setColaboradorData(prev => ({ ...prev, telefone: e.target.value }))}
                  placeholder="(11) 99999-9999"
                />
              </div>

              <div>
                <Label htmlFor="email-colaborador">Email</Label>
                <Input
                  id="email-colaborador"
                  type="email"
                  value={colaboradorData.email}
                  onChange={(e) => setColaboradorData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@exemplo.com"
                />
              </div>

              <div>
                <Label htmlFor="observacoes-colaborador">Observações</Label>
                <Textarea
                  id="observacoes-colaborador"
                  rows={2}
                  value={colaboradorData.observacoes}
                  onChange={(e) => setColaboradorData(prev => ({ ...prev, observacoes: e.target.value }))}
                  placeholder="Observações sobre o colaborador..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveColaborador}>
                Salvar Colaborador
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="profissao" className="space-y-4">
            <div className="space-y-3">
              <div>
                <Label htmlFor="nome-profissao">Nome da Profissão *</Label>
                <Input
                  id="nome-profissao"
                  value={profissaoData.nome}
                  onChange={(e) => setProfissaoData(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Nome da profissão"
                />
              </div>

              <div>
                <Label htmlFor="descricao-profissao">Descrição</Label>
                <Textarea
                  id="descricao-profissao"
                  rows={3}
                  value={profissaoData.descricao}
                  onChange={(e) => setProfissaoData(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder="Descrição da profissão..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveProfissao}>
                Salvar Profissão
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}